import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import Footer from '../components/Footer';
import {
  Package,
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  MessageSquare,
  Globe2,
  PhoneCall,
  Loader2
} from 'lucide-react';
import api from '../config/api';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/misc/contact', formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'support@flywelllogistics.com',
      subtext: 'We reply within 24 hours'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+918826885557',
      subtext: 'Mon-Sat, 10 AM - 6 PM'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Badarpur, Delhi',
      subtext: 'India - 110044'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-indigo-500 selection:text-white">
      <PublicNavbar />


      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-48 pb-20 overflow-hidden bg-slate-900 text-center">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-20 bg-gradient-to-b from-indigo-500 to-transparent blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
            Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-emerald-400">Touch</span>
          </h1>
          <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Our expert logistics team is here to answer your questions. Drop us a message or give us a call.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative -mt-10 z-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 lg:px-12">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 p-8 rounded-2xl text-center shadow-lg hover:-translate-y-2 transition-transform duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-50 border border-indigo-100 rounded-2xl mb-6">
                    <Icon className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-2">{info.title}</h3>
                  {info.emails ? (
                    <div className="space-y-1 mb-1">
                      {info.emails.map((email) => (
                        <a
                          key={email}
                          href={`mailto:${email}`}
                          className="block text-lg text-slate-800 font-bold hover:text-indigo-600 transition-colors"
                        >
                          {email}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-lg text-slate-800 font-bold mb-1">{info.details}</p>
                  )}
                  <p className="text-slate-500 font-medium">{info.subtext}</p>
                </div>
              );
            })}
          </div>

          {/* Additional Contact Emails */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {['info@flywelllogistics.com', 'operations@flywelllogistics.com', 'accounts@flywelllogistics.com'].map((email) => (
              <a
                key={email}
                href={`mailto:${email}`}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 px-6 py-3 rounded-2xl text-slate-800 font-bold shadow-sm hover:border-indigo-400 hover:text-indigo-600 hover:-translate-y-1 transition-all"
              >
                <Mail className="w-4 h-4 text-indigo-600" />
                {email}
              </a>
            ))}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-200">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-indigo-100 p-3 rounded-xl">
                  <MessageSquare className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tight">Send us a Message</h2>
              </div>

              {submitted ? (
                <div className="py-12 text-center bg-emerald-50 rounded-2xl border border-emerald-100">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-black text-emerald-800 mb-2">Message Sent!</h3>
                  <p className="text-emerald-700 font-medium">Thank you. Our support team will respond shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-slate-800 font-medium placeholder-slate-400"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-slate-800 font-medium placeholder-slate-400"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-slate-800 font-medium placeholder-slate-400"
                        placeholder="+91 98765 43210"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-slate-800 font-medium placeholder-slate-400"
                        placeholder="How can we help?"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-slate-800 font-medium placeholder-slate-400 resize-none"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-medium">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-8 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-black text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-indigo-500/30 group disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Business Hours Container */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-slate-500" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-6 tracking-tight">Business Hours</h2>
          <div className="space-y-3 text-lg font-medium text-slate-600">
            <p className="px-6 py-3 bg-slate-50 rounded-xl inline-block mr-2 border border-slate-100">Monday - Friday: 9:00 AM - 6:00 PM</p>
            <p className="px-6 py-3 bg-slate-50 rounded-xl inline-block mr-2 border border-slate-100">Saturday: 10:00 AM - 4:00 PM</p>
            <p className="px-6 py-3 bg-slate-50 text-slate-400 border border-slate-100 rounded-xl inline-block mr-2">Sunday: Closed</p>
          </div>
          <p className="mt-8 text-slate-500 max-w-lg mx-auto">
            For urgent matters outside business hours, please shoot us an email and our dedicated team will respond right away.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
